/* Bastant site 2026 — collection page behaviour (bs-collection-products section).
   - Materiale / Produkttype / Montering filters work in the browser on the products already on the page
     (URL params ?materiale=borstet,sort&type=hylder&montering=skrues-op, so links can be shared).
   - Native Shopify filters (availability, price, sort) are normal links/forms; we only carry our own params along.
   - One card per design: the first product of each custom.link_variants group that matches is shown.
   - "Vis flere" reveals the next batch. Mobile: filters live in a drawer. */
(() => {
  if (window.BastantCollection) return;
  window.BastantCollection = true;

  const KEYS = ['materiale', 'type', 'montering'];
  const mobile = window.matchMedia('(max-width: 999px)');

  const fill = (tpl, n) => (tpl || '[antal]').replace('[antal]', n);

  function init(root) {
    const perPage = parseInt(root.dataset.perPage, 10) || 24;
    const grid = root.querySelector('[data-bs-grid]');
    const items = grid ? Array.from(grid.querySelectorAll('.bs-collection-grid__item')) : [];
    const inputs = Array.from(root.querySelectorAll('input[data-bs-derived]'));
    const stockBtn = root.querySelector('[data-bs-stock-toggle]');
    const pillsList = root.querySelector('[data-bs-pills]');
    const badge = root.querySelector('[data-bs-active-count]');
    const mobileSwatches = root.querySelector('[data-bs-active-swatches]');
    const clearLinks = Array.from(root.querySelectorAll('[data-bs-clear]'));
    const nativeCount = badge ? parseInt(badge.textContent, 10) || 0 : 0;
    const drawer = root.querySelector('[data-bs-drawer]');
    const drawerBtn = root.querySelector('[data-bs-drawer-open]');
    let shown = perPage;

    /* ---------- state <-> URL ---------- */
    const readState = () => {
      const p = new URLSearchParams(window.location.search);
      const s = {};
      KEYS.forEach((k) => {
        s[k] = p.getAll(k).join(',').split(',').map((v) => v.trim()).filter(Boolean);
      });
      s.lager = p.get('lager') === '1';
      return s;
    };
    let state = readState();

    const derivedParams = (url) => {
      KEYS.forEach((k) => {
        url.searchParams.delete(k);
        if (state[k].length) url.searchParams.set(k, state[k].join(','));
      });
      url.searchParams.delete('lager');
      if (state.lager) url.searchParams.set('lager', '1');
      return url;
    };

    const writeUrl = () => {
      const url = derivedParams(new URL(window.location.href));
      window.history.replaceState(window.history.state, '', url.toString());
    };

    const derivedActiveCount = () => KEYS.reduce((n, k) => n + state[k].length, 0) + (state.lager ? 1 : 0);

    /* ---------- render ---------- */
    const matches = (el) =>
      KEYS.every((k) => !state[k].length || state[k].includes(el.dataset[k] || '')) &&
      (!state.lager || el.dataset.available === 'true');

    function apply({ focusFrom } = {}) {
      // One card per design: the first matching in-stock finish, else the first matching finish.
      const hasStock = new Set();
      items.forEach((el) => {
        if (el.dataset.available === 'true' && matches(el)) hasStock.add(el.dataset.group);
      });
      const seen = new Set();
      const reps = [];
      items.forEach((el) => {
        el.removeAttribute('data-later');
        el.removeAttribute('data-sibling');
        const g = el.dataset.group;
        const ok = matches(el) && (el.dataset.available === 'true' || !hasStock.has(g));
        if (!seen.has(g) && ok) {
          seen.add(g);
          reps.push(el);
        } else {
          el.hidden = true;
        }
      });
      reps.forEach((el, i) => { el.hidden = i >= shown; });

      const total = reps.length;
      const text = fill(total === 1 ? root.dataset.countOne : root.dataset.countMany, total);
      root.querySelectorAll('[data-bs-count]').forEach((n) => { n.textContent = text; });
      root.querySelectorAll('[data-bs-drawer-show]').forEach((n) => { n.textContent = `Vis ${text}`; });

      const empty = root.querySelector('[data-bs-empty]');
      if (empty) empty.hidden = total > 0;

      const moreWrap = root.querySelector('[data-bs-more-wrap]');
      if (moreWrap) {
        moreWrap.hidden = total <= shown;
        const progress = moreWrap.querySelector('[data-bs-progress]');
        if (progress) progress.textContent = `Viser ${Math.min(shown, total)} af ${total}`;
      }

      if (typeof focusFrom === 'number' && reps[focusFrom]) {
        const link = reps[focusFrom].querySelector('a');
        if (link) link.focus({ preventScroll: false });
      }

      renderChips();
      renderPills();

      const active = nativeCount + derivedActiveCount();
      if (badge) {
        badge.textContent = active;
        badge.hidden = active === 0;
      }
      clearLinks.forEach((a) => {
        if (a.classList.contains('bs-collection-bar__clear')) a.hidden = active === 0;
      });
      if (mobileSwatches) {
        mobileSwatches.innerHTML = state.materiale
          .map((f) => `<span class="bs-swatch bs-swatch--${f}" aria-hidden="true"></span>`)
          .join('');
      }
      if (stockBtn) {
        stockBtn.classList.toggle('is-on', state.lager);
        stockBtn.setAttribute('aria-checked', String(state.lager));
      }
    }

    function renderChips() {
      root.querySelectorAll('[data-bs-filter]').forEach((details) => {
        const key = details.dataset.bsFilter;
        const checked = inputs.filter((i) => i.dataset.bsDerived === key && i.checked);
        details.classList.toggle('is-active', checked.length > 0);
        const value = details.querySelector('[data-bs-chip-value]');
        if (value && key !== 'materiale') {
          if (!value.dataset.all) value.dataset.all = value.textContent;
          value.textContent = checked.length === 0 ? value.dataset.all
            : checked.length === 1 ? checked[0].dataset.label
            : `${checked.length} valgt`;
        }
        const sw = details.querySelector('.bs-collection-chip__swatches');
        if (sw) {
          sw.classList.toggle('has-selection', checked.length > 0);
          sw.querySelectorAll('[data-bs-chip-swatch]').forEach((s) => {
            s.classList.toggle('is-selected', state.materiale.includes(s.dataset.bsChipSwatch));
          });
        }
      });
    }

    function renderPills() {
      if (!pillsList) return;
      pillsList.querySelectorAll('[data-bs-derived-pill]').forEach((n) => n.remove());
      const add = (label, swatch, onRemove) => {
        const li = document.createElement('li');
        li.setAttribute('data-bs-derived-pill', '');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bs-collection-pill';
        b.innerHTML = `${swatch ? `<span class="bs-swatch bs-swatch--${swatch}" aria-hidden="true"></span>` : ''}<span></span>` +
          '<svg class="bs-icon" width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true"><path d="M4.25 4.25L12.75 12.75M12.75 4.25L4.25 12.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '<span class="bs-sr">Fjern</span>';
        b.querySelector('span:not(.bs-swatch)').textContent = label;
        b.addEventListener('click', onRemove);
        li.appendChild(b);
        pillsList.appendChild(li);
      };
      KEYS.forEach((k) => {
        state[k].forEach((v) => {
          const input = inputs.find((i) => i.dataset.bsDerived === k && i.value === v);
          if (!input) return;
          add(input.dataset.label, input.dataset.swatch, () => {
            input.checked = false;
            onInputChange();
          });
        });
      });
      if (state.lager && stockBtn) {
        add(stockBtn.querySelector('.bs-collection-toggle__label').textContent, '', () => {
          state.lager = false;
          shown = perPage;
          apply();
          writeUrl();
        });
      }
    }

    function onInputChange() {
      KEYS.forEach((k) => {
        state[k] = inputs.filter((i) => i.dataset.bsDerived === k && i.checked).map((i) => i.value);
      });
      shown = perPage;
      apply();
      writeUrl();
    }

    /* ---------- wire up ---------- */
    inputs.forEach((i) => {
      i.checked = state[i.dataset.bsDerived].includes(i.value);
      i.addEventListener('change', onInputChange);
    });
    // Drop values in the URL that do not exist on this collection.
    KEYS.forEach((k) => {
      state[k] = state[k].filter((v) => inputs.some((i) => i.dataset.bsDerived === k && i.value === v));
    });

    if (stockBtn) {
      stockBtn.addEventListener('click', () => {
        state.lager = !state.lager;
        shown = perPage;
        apply();
        writeUrl();
      });
    }

    // Native filter links, sort links, pills: keep our params when leaving.
    root.addEventListener('click', (e) => {
      const a = e.target.closest('a[data-bs-native-link]');
      if (!a || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      const url = derivedParams(new URL(a.href, window.location.href));
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });

    root.querySelectorAll('[data-bs-price-form]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const url = new URL(form.action, window.location.href);
        new FormData(form).forEach((v, k) => {
          if (String(v).trim() !== '') url.searchParams.append(k, v);
        });
        window.location.href = derivedParams(url).toString();
      });
    });

    clearLinks.forEach((a) => {
      a.addEventListener('click', (e) => {
        if (nativeCount > 0) return; // the link drops native filters; our params are not on it either
        e.preventDefault();
        KEYS.forEach((k) => { state[k] = []; });
        state.lager = false;
        inputs.forEach((i) => { i.checked = false; });
        shown = perPage;
        apply();
        writeUrl();
        closeDrawer();
      });
    });

    const more = root.querySelector('[data-bs-more]');
    if (more) {
      more.addEventListener('click', () => {
        const from = shown;
        shown += perPage;
        apply({ focusFrom: from });
      });
    }

    /* Desktop dropdowns: one open at a time, close on outside click / Escape. */
    const allDetails = Array.from(root.querySelectorAll('.bs-collection-filter, .bs-collection-sort'));
    allDetails.forEach((d) => {
      d.addEventListener('toggle', () => {
        if (!d.open) return;
        if (mobile.matches && d.classList.contains('bs-collection-filter')) return;
        allDetails.forEach((o) => { if (o !== d) o.open = false; });
      });
    });
    document.addEventListener('click', (e) => {
      if (mobile.matches && drawer && drawer.contains(e.target)) return;
      allDetails.forEach((d) => { if (d.open && !d.contains(e.target)) d.open = false; });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (drawer && drawer.classList.contains('is-open')) { closeDrawer(); return; }
      allDetails.forEach((d) => {
        if (d.open) {
          d.open = false;
          const s = d.querySelector('summary');
          if (s) s.focus();
        }
      });
    });

    /* Mobile drawer */
    const panel = drawer ? drawer.querySelector('.bs-collection-drawer__panel') : null;
    function openDrawer() {
      if (!drawer) return;
      drawer.classList.add('is-open');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      drawerBtn && drawerBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('bs-lock');
      const close = drawer.querySelector('.bs-collection-drawer__close');
      if (close) close.focus();
    }
    function closeDrawer() {
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      drawerBtn && drawerBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('bs-lock');
      drawerBtn && drawerBtn.focus();
    }
    if (drawerBtn) drawerBtn.addEventListener('click', openDrawer);
    root.querySelectorAll('[data-bs-drawer-close]').forEach((b) => b.addEventListener('click', closeDrawer));
    const onMq = () => { if (!mobile.matches) closeDrawer(); };
    if (mobile.addEventListener) mobile.addEventListener('change', onMq);

    apply();
    if (derivedActiveCount()) writeUrl();
  }

  const start = () => document.querySelectorAll('[data-bs-collection]').forEach((root) => {
    if (root.dataset.bsReady) return;
    root.dataset.bsReady = '1';
    init(root);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
